import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { Card, CardActionArea, Grid, useTheme, alpha, Chip, Typography } from "@mui/material";
import MuiLink from "@mui/material/Link";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";
import MKBadge from "components/MKBadge";
import zIndex from "@mui/material/styles/zIndex";

/**
 * CSTCenteredCard is a React component that displays a card with an image,
 * title, position badges, description, and a call-to-action button.
 *
 * Props:
 * @param {string} image - The URL of the image to display at the top of the card.
 * @param {string} title - The title text displayed prominently on the card.
 * @param {Object} position - An object containing position labels and color settings
 *                            for rendering badges.
 * @param {string} description - A brief textual description displayed on the card.
 * @param {Object} action - An object containing the action details such as label, type,
 *                          route, and color for the CTA button.
 * @param {Function} onClickCallback - A callback function triggered when the card is clicked.
 * @param {Object} [style={}] - Additional styling to customize the card appearance.
 *
 * The component uses Material-UI components and theming to create a visually appealing
 * card layout with hover effects and dynamic content rendering.
 */

function CSTCenteredCard({
  image,
  title,
  position,
  description,
  action,
  onClickCallback,
  style = {},
}) {
  const theme = useTheme();

  const renderBadges = () => {
    const theme = useTheme();

    if (!position || !position.labels || Object.keys(position.labels).length === 0) return null;

    return (
      <Grid container spacing={1.5} mt={2} justifyContent="center">
        {Object.entries(position.labels).map(([key, val], idx) => (
          <Grid item key={idx}>
            <Chip
              label={
                <Typography variant="caption" fontWeight="medium" sx={{ color: "#fff" }}>
                  {`${key.toUpperCase()}: ${val}`}
                </Typography>
              }
              sx={{
                backgroundColor: theme.palette[position.color || "info"].main,
                color: theme.palette.getContrastText(theme.palette[position.color || "info"].main),
                px: 2,
                py: 0.5,
                fontSize: "0.75rem",
                borderRadius: "999px",
                boxShadow: `0 2px 6px ${alpha(theme.palette[position.color || "info"].main, 0.45)}`,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  boxShadow: `0 4px 10px ${alpha(
                    theme.palette[position.color || "info"].main,
                    0.55
                  )}`,
                  transform: "scale(1.05)",
                },
              }}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderButton = () => {
    const commonProps = {
      variant: "gradient",
      size: "medium",
      fullWidth: true,
      color: action.color || "dark",
      children: action.label,
      sx: {
        mt: 2,
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "scale(1.05)",
          boxShadow: theme.shadows[4],
        },
      },
    };

    return action.type === "external" ? (
      <MKButton
        component={MuiLink}
        href={action.route}
        target="_blank"
        rel="noreferrer"
        {...commonProps}
      />
    ) : (
      <MKButton component={Link} to={action.route} {...commonProps} />
    );
  };

  return (
    <Card
      sx={{
        my: 4,
        borderRadius: 4,
        position: "relative",
        backdropFilter: "blur(8px)",
        backgroundColor: alpha(theme.palette.background.paper, 0.85),
        boxShadow: "0 8px 16px rgba(0, 0, 0, 0.06), 0 4px 8px rgba(0, 0, 0, 0.04)",
        transition: "transform 0.3s ease",
        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: "0 12px 32px rgba(0, 0, 0, 0.12)",
        },
        ...style,
      }}
    >
      <CardActionArea onClick={onClickCallback}>
        {/* Image Section */}

        <MKBox position="relative" borderRadius="lg" mx={2} mt={-3}>
          <MKBox
            component="img"
            src={image}
            alt={title}
            borderRadius="lg"
            width="100%"
            position="relative"
            zIndex={3}
          />
          <MKBox
            borderRadius="lg"
            shadow="md"
            width="100%"
            height="100%"
            position="absolute"
            left={0}
            top={0}
            sx={{
              backgroundImage: `url(${image})`,
              transform: "scale(0.94)",
              filter: "blur(12px)",
              backgroundSize: "cover",
            }}
          />
        </MKBox>

        {/* Content */}
        <MKBox px={3} py={3} textAlign="center" zIndex={1} position="relative">
          <MKTypography variant="h4" fontWeight="bold" color="primary" gutterBottom>
            {title}
          </MKTypography>

          {/* Badges */}
          {renderBadges()}

          {/* Description */}
          <MKTypography variant="body2" color="secondary" mt={2} sx={{ minHeight: "60px" }}>
            {description}
          </MKTypography>

          {/* CTA */}
          {renderButton()}
        </MKBox>
      </CardActionArea>
    </Card>
  );
}

CSTCenteredCard.propTypes = {
  image: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  position: PropTypes.shape({
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
    labels: PropTypes.objectOf(PropTypes.string),
  }).isRequired,
  action: PropTypes.shape({
    type: PropTypes.oneOf(["external", "internal"]).isRequired,
    route: PropTypes.string.isRequired,
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
    label: PropTypes.string.isRequired,
  }).isRequired,
};

export default CSTCenteredCard;
